import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Check, X, Trash2, Pencil, ExternalLink, GitCompareArrows } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { AiInsightCard } from '../../components/ui/AiInsightCard';
import { PageSignalsLink } from '../../components/PageSignalsLink';
import { Pill } from '../../components/ui/Pill';
import { Drawer } from '../../components/ui/Drawer';
import type { PurchaseOrder } from '../../hooks/useMatching';
import { useVendors } from '../../hooks/useVendors';
import { useApInvoices } from '../../hooks/useApInvoices';
import {
  usePurchaseOrders,
  useCreatePurchaseOrder,
  useUpdatePurchaseOrder,
  useDeletePurchaseOrder,
  useDeliveryNotes,
  useCreateDeliveryNote,
  useUpdateDeliveryNote,
  useDeleteDeliveryNote,
  useInspectDelivery,
  type PoLineItem,
  type DeliveryNote,
} from '../../hooks/useMatching';
import { QueryError } from '../../components/ui/QueryError';
import { EmptyState } from '../../components/ui/EmptyState';
import { IntroCallout } from '../../components/ui/IntroCallout';
import { formatAmount, formatDate } from '../../lib/format';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { useCan } from '../../hooks/useCan';

type Tone = 'neutral' | 'accent' | 'success' | 'warn' | 'muted';
const PO_TONE: Record<string, Tone> = { open: 'accent', closed: 'muted', cancelled: 'warn' };
const INSPECT_TONE: Record<string, Tone> = { pending: 'accent', accepted: 'success', rejected: 'warn' };

const emptyLine = (): PoLineItem => ({ description: '', qty: 1, unitPriceCents: 0, taxRate: 10 });

function LineItemsEditor({ lines, onChange }: { lines: PoLineItem[]; onChange: (l: PoLineItem[]) => void }) {
  const { t } = useTranslation();
  const update = (i: number, patch: Partial<PoLineItem>) =>
    onChange(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  return (
    <div className="space-y-2">
      {lines.map((l, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            value={l.description}
            onChange={(e) => update(i, { description: e.target.value })}
            placeholder={t('inbox.col.description')}
            className="flex-1 px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
          />
          <input
            type="number"
            value={l.qty}
            onChange={(e) => update(i, { qty: Number(e.target.value) })}
            title={t('inbox.col.qty')}
            className="w-16 px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 num"
          />
          <input
            type="number"
            value={l.unitPriceCents}
            onChange={(e) => update(i, { unitPriceCents: Number(e.target.value) })}
            title={t('invoicesAr.unitCents')}
            className="w-28 px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 num"
          />
          <input
            type="number"
            value={l.taxRate ?? 0}
            onChange={(e) => update(i, { taxRate: Number(e.target.value) })}
            title={t('invoicesAr.taxRate')}
            className="w-16 px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 num"
          />
          <button
            type="button"
            onClick={() => onChange(lines.filter((_, idx) => idx !== i))}
            className="p-1.5 text-ink-400 hover:text-warn-600"
            aria-label={t('matching.removeLine')}
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...lines, emptyLine()])}
        className="text-xs text-accent-600 hover:text-accent-700 inline-flex items-center gap-1"
      >
        <Plus size={12} /> {t('matching.addLine')}
      </button>
    </div>
  );
}

export function MatchingPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { canMutate } = useCan();
  const confirm = useConfirm();
  const vendors = useVendors(wsId);
  const pos = usePurchaseOrders(wsId);
  // For the reverse link (matching → AP): which AP invoices reference each PO.
  const apInvoices = useApInvoices(wsId);
  const [searchParams, setSearchParams] = useSearchParams();
  const deliveries = useDeliveryNotes(wsId);
  const createPo = useCreatePurchaseOrder(wsId);
  const createDelivery = useCreateDeliveryNote(wsId);
  const inspect = useInspectDelivery(wsId);
  const deletePo = useDeletePurchaseOrder(wsId);
  const deleteDelivery = useDeleteDeliveryNote(wsId);
  const updatePo = useUpdatePurchaseOrder(wsId);
  const updateDelivery = useUpdateDeliveryNote(wsId);
  const [editingPoId, setEditingPoId] = useState<string | null>(null);
  const [editingDnId, setEditingDnId] = useState<string | null>(null);

  const openEditPo = (po: PurchaseOrder) => {
    setEditingPoId(po.id);
    setPoNumber(po.po_number);
    setPoVendor(po.vendor_id ?? '');
    setPoCurrency(po.currency);
    setPoLines(po.line_items?.length ? po.line_items : [emptyLine()]);
    setPoForm(true);
    openDetailPo(null);
  };
  const openEditDn = (dn: DeliveryNote) => {
    setEditingDnId(dn.id);
    setDnPo(dn.po_id ?? '');
    setDnNumber(dn.delivery_number ?? '');
    setDnDate(dn.received_date ?? '');
    setDnLines(dn.line_items?.length ? dn.line_items : [emptyLine()]);
    setDnForm(true);
  };

  const onDeletePo = async (id: string, num: string) => {
    const ok = await confirm({ title: t('matching.confirmDeletePo', { num }), confirmLabel: t('common.delete'), destructive: true });
    if (!ok) return;
    try {
      await deletePo.mutateAsync(id);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Delete failed');
    }
  };

  const [poForm, setPoForm] = useState(false);
  const [dnForm, setDnForm] = useState(false);
  const [detailPo, setDetailPo] = useState<PurchaseOrder | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Deep-link support: the AP invoice drawer links to `…/matching?po=<id>` so a
  // user can jump from a bill straight to its purchase order (the reverse of the
  // drawer → matching link). Open the PO detail once the POs have loaded.
  useEffect(() => {
    const poId = searchParams.get('po');
    if (poId && pos.data && (!detailPo || detailPo.id !== poId)) {
      const po = pos.data.find((p) => p.id === poId);
      if (po) setDetailPo(po);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, pos.data]);
  const openDetailPo = (po: PurchaseOrder | null) => {
    setDetailPo(po);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (po) next.set('po', po.id);
        else next.delete('po');
        return next;
      },
      { replace: true },
    );
  };

  const [poNumber, setPoNumber] = useState('');
  const [poVendor, setPoVendor] = useState('');
  const [poCurrency, setPoCurrency] = useState('JPY');
  const [poLines, setPoLines] = useState<PoLineItem[]>([emptyLine()]);

  const [dnPo, setDnPo] = useState('');
  const [dnNumber, setDnNumber] = useState('');
  const [dnDate, setDnDate] = useState('');
  const [dnLines, setDnLines] = useState<PoLineItem[]>([emptyLine()]);

  const vendorName = (id: string | null) =>
    vendors.data?.find((v) => v.id === id)?.display_name ||
    vendors.data?.find((v) => v.id === id)?.legal_name ||
    '—';

  const submitPo = async () => {
    setError(null);
    if (!poNumber.trim()) return setError(t('matching.errorPoNumberRequired'));
    const lines = poLines.filter((l) => l.description.trim());
    if (!lines.length) return setError(t('matching.errorAtLeastOneLine'));
    try {
      const body = { poNumber, vendorId: poVendor || undefined, currency: poCurrency, lineItems: lines };
      if (editingPoId) await updatePo.mutateAsync({ id: editingPoId, patch: body });
      else await createPo.mutateAsync(body);
      setPoForm(false);
      setEditingPoId(null);
      setPoNumber('');
      setPoVendor('');
      setPoLines([emptyLine()]);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed');
    }
  };

  const submitDn = async () => {
    setError(null);
    const lines = dnLines.filter((l) => l.description.trim());
    if (!lines.length) return setError(t('matching.errorAtLeastOneLine'));
    try {
      const body = {
        poId: dnPo || undefined,
        deliveryNumber: dnNumber || undefined,
        receivedDate: dnDate || undefined,
        currency: poCurrency,
        lineItems: lines,
      };
      if (editingDnId) await updateDelivery.mutateAsync({ id: editingDnId, patch: body });
      else await createDelivery.mutateAsync(body);
      setDnForm(false);
      setEditingDnId(null);
      setDnNumber('');
      setDnDate('');
      setDnPo('');
      setDnLines([emptyLine()]);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed');
    }
  };

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <h1 className="display text-2xl font-semibold">{t('matching.title')}</h1>
            <PageSignalsLink kinds={['unmatched_ap']} />
          </div>
          <p className="text-sm text-ink-500 mt-1">{t('matching.subtitle')}</p>
        </div>

        <IntroCallout storageKey="matching" title={t('intro.matching.title')} body={t('intro.matching.body')} />

        {error && (
          <div className="mb-4 text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {error}
          </div>
        )}

        <div className="mb-6">
          <AiInsightCard
            agent="anomaly"
            title={t('aiOutput.cards.matchReview')}
            prompt={t('aiOutput.prompts.matchReview')}
          />
        </div>

        {/* Purchase orders */}
        <Card className="mb-6">
          <div className="px-5 py-3 border-b border-ink-200 dark:border-ink-800 flex items-center justify-between">
            <span className="font-medium">{t('matching.purchaseOrders')}</span>
            {canMutate && (
              <button
                type="button"
                onClick={() => { setEditingPoId(null); setPoNumber(''); setPoVendor(''); setPoLines([emptyLine()]); setPoForm((o) => !o); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700"
              >
                <Plus size={12} /> {t('matching.newPo')}
              </button>
            )}
          </div>
          {poForm && (
            <div className="p-5 border-b border-ink-200 dark:border-ink-800 bg-ink-50 dark:bg-ink-900/40 space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <input
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder={t('matching.poNumber')}
                  className="px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
                />
                <select
                  value={poVendor}
                  onChange={(e) => setPoVendor(e.target.value)}
                  className="px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
                >
                  <option value="">{t('matching.selectVendor')}</option>
                  {(vendors.data ?? []).map((v) => (
                    <option key={v.id} value={v.id}>{v.display_name || v.legal_name}</option>
                  ))}
                </select>
                <input
                  value={poCurrency}
                  onChange={(e) => setPoCurrency(e.target.value.toUpperCase())}
                  placeholder="JPY"
                  className="px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
                />
              </div>
              <LineItemsEditor lines={poLines} onChange={setPoLines} />
              <button
                type="button"
                onClick={submitPo}
                disabled={createPo.isPending}
                className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
              >
                {t('common.save')}
              </button>
            </div>
          )}
          {pos.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : pos.isError ? (
            <QueryError onRetry={pos.refetch} />
          ) : !pos.data?.length ? (
            <EmptyState
              icon={GitCompareArrows}
              title={t('emptyState.matching.title')}
              description={t('emptyState.matching.body')}
              primaryLabel={t('matching.newPo')}
              primaryIcon={Plus}
              onPrimary={() => { setEditingPoId(null); setPoNumber(''); setPoVendor(''); setPoLines([emptyLine()]); setPoForm(true); }}
              offerSampleData
              wsId={wsId}
            />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-ink-500 border-b border-ink-200 dark:border-ink-800">
                  <th className="px-5 py-2 font-medium">{t('matching.poNumber')}</th>
                  <th className="px-5 py-2 font-medium">{t('matching.vendor')}</th>
                  <th className="px-5 py-2 font-medium">{t('common.status')}</th>
                  <th className="px-5 py-2 font-medium text-right">{t('common.amount')}</th>
                  <th className="px-5 py-2 font-medium text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pos.data.map((po) => (
                  <tr key={po.id} className="border-b border-ink-200 dark:border-ink-800">
                    <td className="px-5 py-2.5 font-medium">
                      <button type="button" onClick={() => openDetailPo(po)} className="text-accent-600 hover:underline">{po.po_number}</button>
                    </td>
                    <td className="px-5 py-2.5">{vendorName(po.vendor_id)}</td>
                    <td className="px-5 py-2.5"><Pill tone={PO_TONE[po.status]}>{t(`matching.poStatus.${po.status}`)}</Pill></td>
                    <td className="px-5 py-2.5 text-right num">{formatAmount(po.total_cents, po.currency, i18n.language)}</td>
                    <td className="px-5 py-2.5 text-right">
                      {canMutate ? (
                        <button type="button" onClick={() => onDeletePo(po.id, po.po_number)} className="p-1.5 rounded-md text-ink-400 hover:text-warn-600" title={t('common.delete')}>
                          <Trash2 size={14} />
                        </button>
                      ) : (
                        <span className="text-ink-300 dark:text-ink-700">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Delivery notes / 検収 */}
        <Card>
          <div className="px-5 py-3 border-b border-ink-200 dark:border-ink-800 flex items-center justify-between">
            <span className="font-medium">{t('matching.deliveryNotes')}</span>
            {canMutate && (
              <button
                type="button"
                onClick={() => { setEditingDnId(null); setDnPo(''); setDnNumber(''); setDnDate(''); setDnLines([emptyLine()]); setDnForm((o) => !o); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700"
              >
                <Plus size={12} /> {t('matching.newDelivery')}
              </button>
            )}
          </div>
          {dnForm && (
            <div className="p-5 border-b border-ink-200 dark:border-ink-800 bg-ink-50 dark:bg-ink-900/40 space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <select
                  value={dnPo}
                  onChange={(e) => setDnPo(e.target.value)}
                  className="px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
                >
                  <option value="">{t('matching.linkPo')}</option>
                  {(pos.data ?? []).map((po) => (
                    <option key={po.id} value={po.id}>{po.po_number}</option>
                  ))}
                </select>
                <input
                  value={dnNumber}
                  onChange={(e) => setDnNumber(e.target.value)}
                  placeholder={t('matching.deliveryNumber')}
                  className="px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
                />
                <input
                  type="date"
                  value={dnDate}
                  onChange={(e) => setDnDate(e.target.value)}
                  className="px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
                />
              </div>
              <LineItemsEditor lines={dnLines} onChange={setDnLines} />
              <button
                type="button"
                onClick={submitDn}
                disabled={createDelivery.isPending}
                className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
              >
                {t('common.save')}
              </button>
            </div>
          )}
          {deliveries.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : deliveries.isError ? (
            <QueryError onRetry={deliveries.refetch} />
          ) : !deliveries.data?.length ? (
            <div className="p-10 text-center text-ink-500 text-sm">{t('matching.noDeliveries')}</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-ink-500 border-b border-ink-200 dark:border-ink-800">
                  <th className="px-5 py-2 font-medium">{t('matching.deliveryNumber')}</th>
                  <th className="px-5 py-2 font-medium">{t('common.date')}</th>
                  <th className="px-5 py-2 font-medium">{t('matching.inspection')}</th>
                  <th className="px-5 py-2 font-medium text-right">{t('common.amount')}</th>
                  <th className="px-5 py-2 font-medium text-right">{t('matching.inspect')}</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.data.map((dn) => (
                  <tr key={dn.id} className="border-b border-ink-200 dark:border-ink-800">
                    <td className="px-5 py-2.5 font-medium">{dn.delivery_number ?? '—'}</td>
                    <td className="px-5 py-2.5">{formatDate(dn.received_date, i18n.language)}</td>
                    <td className="px-5 py-2.5">
                      <Pill tone={INSPECT_TONE[dn.inspection_status]}>{t(`matching.inspectStatus.${dn.inspection_status}`)}</Pill>
                    </td>
                    <td className="px-5 py-2.5 text-right num">{formatAmount(dn.total_cents, dn.currency, i18n.language)}</td>
                    <td className="px-5 py-2.5 text-right">
                      {!canMutate ? (
                        <span className="text-ink-300 dark:text-ink-700">—</span>
                      ) : (
                      <div className="inline-flex gap-1">
                        {dn.inspection_status === 'pending' && (
                          <>
                            <button
                              type="button"
                              onClick={() => inspect.mutate({ id: dn.id, decision: 'accepted' })}
                              className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                              title={t('matching.accept')}
                            >
                              <Check size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => inspect.mutate({ id: dn.id, decision: 'rejected' })}
                              className="p-1.5 rounded-md text-warn-600 hover:bg-warn-50 dark:hover:bg-warn-900/30"
                              title={t('matching.reject')}
                            >
                              <X size={14} />
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => openEditDn(dn)}
                          className="p-1.5 rounded-md text-ink-400 hover:text-accent-600"
                          title={t('common.edit')}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={async () => { if (await confirm({ title: t('common.confirmDelete'), confirmLabel: t('common.delete'), destructive: true })) deleteDelivery.mutate(dn.id); }}
                          className="p-1.5 rounded-md text-ink-400 hover:text-warn-600"
                          title={t('common.delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <Drawer
        open={!!detailPo}
        onOpenChange={(o) => !o && openDetailPo(null)}
        title={detailPo?.po_number ?? ''}
        description={detailPo ? `${vendorName(detailPo.vendor_id)} · ${formatAmount(detailPo.total_cents, detailPo.currency, i18n.language)}` : undefined}
        footer={
          detailPo && canMutate ? (
            <div className="flex justify-end">
              <button type="button" onClick={() => openEditPo(detailPo)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900">
                <Pencil size={12} /> {t('common.edit')}
              </button>
            </div>
          ) : null
        }
      >
        {detailPo && (
          <div className="space-y-5 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><div className="text-[11px] uppercase text-ink-500">{t('common.status')}</div><Pill tone={PO_TONE[detailPo.status]}>{t(`matching.poStatus.${detailPo.status}`)}</Pill></div>
              <div><div className="text-[11px] uppercase text-ink-500">{t('common.issue')}</div><div>{formatDate(detailPo.issue_date, i18n.language)}</div></div>
            </div>
            <div>
              <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">{t('inbox.lineItems')}</h3>
              <div className="rounded-lg border border-ink-200 dark:border-ink-800 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-ink-50 dark:bg-ink-900/50 text-left">
                    <tr><th className="px-3 py-2">{t('inbox.col.description')}</th><th className="px-3 py-2 text-right">{t('inbox.col.qty')}</th><th className="px-3 py-2 text-right">{t('inbox.col.unit')}</th><th className="px-3 py-2 text-right">{t('invoicesAr.taxRate')}</th></tr>
                  </thead>
                  <tbody className="divide-y divide-ink-200 dark:divide-ink-800">
                    {(detailPo.line_items ?? []).map((li, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{li.description}</td>
                        <td className="px-3 py-2 text-right num">{li.qty}</td>
                        <td className="px-3 py-2 text-right num">{formatAmount(li.unitPriceCents, detailPo.currency, i18n.language)}</td>
                        <td className="px-3 py-2 text-right num">{li.taxRate ?? 0}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">{t('matching.deliveryNotes')}</h3>
              {(deliveries.data ?? []).filter((dn) => dn.po_id === detailPo.id).length === 0 ? (
                <div className="text-xs text-ink-500">{t('matching.noDeliveries')}</div>
              ) : (
                <ul className="space-y-1">
                  {(deliveries.data ?? []).filter((dn) => dn.po_id === detailPo.id).map((dn) => (
                    <li key={dn.id} className="flex items-center justify-between text-xs rounded-md border border-ink-200 dark:border-ink-800 px-2 py-1.5">
                      <span>{dn.delivery_number ?? '—'} · {formatDate(dn.received_date, i18n.language)}</span>
                      <Pill tone={INSPECT_TONE[dn.inspection_status]}>{t(`matching.inspectStatus.${dn.inspection_status}`)}</Pill>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">{t('matching.linkedApInvoices')}</h3>
              {(() => {
                const linked = (apInvoices.data ?? []).filter((inv) => inv.po_id === detailPo.id);
                if (linked.length === 0) return <div className="text-xs text-ink-500">{t('matching.noLinkedAp')}</div>;
                return (
                  <ul className="space-y-1">
                    {linked.map((inv) => (
                      <li key={inv.id}>
                        <button
                          type="button"
                          onClick={() => navigate(`/app/workspace/${wsId}/ap-invoices?open=${inv.id}`)}
                          className="w-full flex items-center justify-between gap-2 text-xs rounded-md border border-ink-200 dark:border-ink-800 px-2 py-1.5 hover:bg-ink-50 dark:hover:bg-ink-900 text-left"
                        >
                          <span className="inline-flex items-center gap-1.5 text-accent-600">
                            <ExternalLink size={12} />
                            {inv.invoice_number ?? inv.id.slice(0, 8)}
                          </span>
                          <span className="num text-ink-500">{formatAmount(inv.total_cents, inv.currency, i18n.language)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          </div>
        )}
      </Drawer>
    </AppShell>
  );
}
